/**
 * Campaign Status Guard
 * Prevents manual access to campaign-ended and campaign-countdown pages
 * when campaign status doesn't match the expected state
 */

async function campaignStatusGuard(to, from, next) {
  // Only check for campaign-ended and campaign-countdown routes
  if (to.name !== 'campaign-ended' && to.name !== 'campaign-countdown') {
    return next()
  }

  try {
    // Check campaign status from backend
    const response = await fetch('/api/campaign/status', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      console.error('Failed to fetch campaign status:', response.status)
      // On error, redirect to home for safety
      return next({ name: 'welcome' })
    }

    const data = await response.json()

    // Handle campaign-ended page access
    if (to.name === 'campaign-ended') {
      // Only allow access to campaign-ended page if campaign has actually expired
      if (data.status !== 'expired') {
        console.log(`Campaign status is '${data.status}', redirecting to home`)
        return next({ name: 'welcome' })
      }

      // Check for legitimate access token (set by handleStartNow in welcome.vue)
      const legitimateAccess = sessionStorage.getItem('legitimateCampaignEndedAccess')
      const accessTimestamp = sessionStorage.getItem('campaignEndedAccessTimestamp')

      // Validate access token - must exist and be recent (within 5 minutes)
      const isLegitimateAccess = legitimateAccess === 'true' &&
                                 accessTimestamp &&
                                 (Date.now() - parseInt(accessTimestamp)) < 300000 // 5 minutes

      if (!isLegitimateAccess) {
        console.log('Unauthorized direct access to campaign-ended page, redirecting to welcome')
        return next({ name: 'welcome' })
      }

      // Clear the access token after use
      sessionStorage.removeItem('legitimateCampaignEndedAccess')
      sessionStorage.removeItem('campaignEndedAccessTimestamp')

      // Campaign has ended and access is legitimate, allow access to the page
      console.log('Campaign has ended and access is legitimate, allowing access to campaign-ended page')
      next()
    }
    // Handle campaign-countdown page access
    else if (to.name === 'campaign-countdown') {
      // Only allow access to campaign-countdown page if campaign hasn't started yet
      if (data.status !== 'upcoming') {
        console.log(`Campaign status is '${data.status}', redirecting to home`)
        return next({ name: 'welcome' })
      }

      // Check for legitimate access token (set by handleStartNow in welcome.vue)
      const legitimateAccess = sessionStorage.getItem('legitimateCampaignCountdownAccess')
      const accessTimestamp = sessionStorage.getItem('campaignCountdownAccessTimestamp')

      // Validate access token - must exist and be recent (within 5 minutes)
      const isLegitimateAccess = legitimateAccess === 'true' &&
                                 accessTimestamp &&
                                 (Date.now() - parseInt(accessTimestamp)) < 300000 // 5 minutes

      if (!isLegitimateAccess) {
        console.log('Unauthorized direct access to campaign-countdown page, redirecting to welcome')
        return next({ name: 'welcome' })
      }

      // Clear the access token after use
      sessionStorage.removeItem('legitimateCampaignCountdownAccess')
      sessionStorage.removeItem('campaignCountdownAccessTimestamp')

      // Campaign hasn't started and access is legitimate, allow access to the countdown page
      console.log('Campaign is upcoming and access is legitimate, allowing access to campaign-countdown page')
      next()
    }

  } catch (error) {
    console.error('Error checking campaign status:', error)
    // On error, redirect to home for safety
    return next({ name: 'welcome' })
  }
}

export default campaignStatusGuard
